import { useTranslation } from 'react-i18next';

import type { BillingStatus } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import { formatBillingDate, type BillingUiState } from '../lib/billingState';
import styles from './CurrentPlanCard.module.css';

interface CurrentPlanCardProps {
  status: BillingStatus;
  uiState: BillingUiState;
  /** Действия недоступны, пока идёт ожидание активации (AC-6). */
  isBusy: boolean;
  onCancel: () => void;
  onResume: () => void;
}

// PARA-131 (AC-2, AC-12, AC-13) — текущий тариф, использование квоты и срок.
// Формулировка блока даты следует auto_renew: «продлится» и «доступ до» — это
// одна и та же дата с разным смыслом, нейтральное «до <дата>» недопустимо.
export function CurrentPlanCard({
  status,
  uiState,
  isBusy,
  onCancel,
  onResume,
}: CurrentPlanCardProps): JSX.Element {
  const { t, i18n } = useTranslation('billing');

  const date = status.expires_at ? formatBillingDate(status.expires_at, i18n.language) : null;
  const quotaRatio =
    status.task_limit !== null && status.task_limit > 0
      ? Math.min(status.task_count / status.task_limit, 1)
      : null;

  return (
    <section className={styles.card} data-testid="current-plan-card">
      <span className={styles.label}>{t('currentPlan.label')}</span>
      <h2 className={styles.plan}>{t(`plans.${status.plan}.name`)}</h2>

      <p className={styles.quota}>
        {status.task_limit === null
          ? t('currentPlan.quotaUnlimited')
          : t('currentPlan.quota', { used: status.task_count, limit: status.task_limit })}
      </p>
      {quotaRatio !== null && (
        <div className={styles.quotaTrack} data-testid="quota-indicator">
          <div className={styles.quotaFill} style={{ width: `${quotaRatio * 100}%` }} />
        </div>
      )}

      {/* AC-2: при expires_at === null блока даты нет вовсе, а не «—». */}
      {date !== null && (
        <p className={styles.date} data-testid="plan-expiry">
          {uiState === 'active-renewing' && t('currentPlan.renewsOn', { date })}
          {uiState === 'cancelled-with-access' && t('currentPlan.accessUntilCancelled', { date })}
          {(uiState === 'pending-invoice' || uiState === 'active-not-renewing') &&
            t('currentPlan.accessUntil', { date })}
        </p>
      )}

      {/* AC-12 / Решение Р-10: видимость определяет auto_renew, а не статус — у
          legacy stub-подписок нажатие ничего не изменило бы у провайдера. */}
      {uiState === 'active-renewing' && (
        <Button variant="ghost" fullWidth disabled={isBusy} onClick={onCancel}>
          {t('currentPlan.cancel')}
        </Button>
      )}
      {uiState === 'cancelled-with-access' && (
        <Button variant="secondary" fullWidth disabled={isBusy} onClick={onResume}>
          {t('currentPlan.resume')}
        </Button>
      )}
    </section>
  );
}
